import { useEffect, useState } from 'react';

import { Select, InputNumber, Button, Form } from 'antd';
import { Row as BaseRow, Col as BaseCol } from 'antd';
import {
  getPricing,
  getProductCategoryOptions,
  getProductOptions,
  getProductVarietyOptions,
  getServiceChargeOptions,
} from '@app/api/nominalPayments.api';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

const { Option } = Select;

interface ExportItemFormProps {
  onAdd: (item: any) => void;
  departmentOptions: { label: string; value: string }[];
}

const ExportItemForm: React.FC<ExportItemFormProps> = ({ onAdd, departmentOptions }) => {
  const [form] = Form.useForm();
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>();
  const [categoryOptions, setCategoryOptions] = useState<
    { value: number; label: string; uom: string; unit_price: number }[]
  >([]);
  const [productOptions, setProductOptions] = useState<
    { value: number; label: string; uom: string; unit_price: number }[]
  >([]);
  const [varietyOptions, setVarietyOptions] = useState<
    { value: number; label: string; uom: string; unit_price: number }[]
  >([]);
  const [serviceChargeOptions, setServiceChargeOptions] = useState<
    { value: number; label: string; unit_price: number }[]
  >([]);

  const handleErrorMessages = useApiErrorHandler();

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();

      const { product_category, product, product_variety, qty, service_charge, lcl, fcl } = values;

      const selectedCategory = categoryOptions.find((c) => c.value === product_category);
      const selectedProduct = productOptions.find((p) => p.value === product);
      const selectedVariety = varietyOptions.find((v) => v.value === product_variety);

      const varietyId = product_variety || null;
      const days = 0;
      let unit_price = 0;
      let uom = '';

      const pricingModel =
        selectedDepartment === 6 ? 'SERVICE_CHARGE' : selectedDepartment === 7 ? 'PRODUCT' : 'PRODUCT';

      const labServiceId = null;
      const serviceChargeId = service_charge || null;

      const pricingResponse = await getPricing(
        'EXPORT',
        product_category,
        product,
        varietyId,
        selectedDepartment!,
        days,
        qty,
        pricingModel,
        labServiceId,
        serviceChargeId,
      );

      const pricingData = pricingResponse?.data;
      unit_price = parseFloat(pricingData?.unit_price || '0');
      uom = pricingData?.uom || '';

      onAdd({
        key: `item-${Date.now()}`,
        department_id: selectedDepartment,
        departmentLabel: values.departmentLabel,
        product_category: selectedCategory?.label || '',
        product: selectedProduct?.label || '',
        product_variety: selectedVariety?.label || '',
        product_category_id: selectedCategory?.value,
        product_id: selectedProduct?.value,
        product_variety_id: selectedVariety?.value,
        uom,
        unit_price,
        qty,
        lcl,
        fcl,
        serviceCharge: values.serviceChargeLabel || '',
        service_charge_id: service_charge,
      });

      form.resetFields();
      setSelectedDepartment(null);
      setCategoryOptions([]);
      setProductOptions([]);
      setVarietyOptions([]);
    } catch (err) {
      handleErrorMessages(err);
    }
  };

  const fetchCategoryOptions = async (departmentId: number) => {
    try {
      const res = await getProductCategoryOptions(departmentId, 'EXPORT');
      setCategoryOptions(res.product_category_options || []);
      setProductOptions([]);
      setVarietyOptions([]);
      form.setFieldsValue({ product_category: undefined, product: undefined, product_variety: undefined });
    } catch (err) {
      handleErrorMessages(err);

      setCategoryOptions([]);
    }
  };

  const fetchProductOptions = async (categoryId: number) => {
    try {
      const res = await getProductOptions(categoryId, 'EXPORT');
      setProductOptions(res.product_options || []);
      setVarietyOptions([]);
      form.setFieldsValue({ product: undefined, product_variety: undefined });
    } catch (err) {
      handleErrorMessages(err);

      setProductOptions([]);
    }
  };

  const fetchVarietyOptions = async (productId: number) => {
    try {
      const res = await getProductVarietyOptions(productId, 'EXPORT');
      setVarietyOptions(res.product_variety_options || []);
      form.setFieldsValue({ product_variety: undefined });
    } catch (err) {
      handleErrorMessages(err);

      setVarietyOptions([]);
    }
  };

  const fetchServiceChargeOptions = async () => {
    try {
      const res = await getServiceChargeOptions('EXPORT');
      setServiceChargeOptions(res.service_charge_options || []);
      form.setFieldsValue({ service_charge: undefined });
    } catch (err) {
      handleErrorMessages(err);

      setServiceChargeOptions([]);
    }
  };

  return (
    <Form
      form={form}
      name="EportItemsForm"
      size="small"
      initialValues={{ qty: 1, checkedCount: 1 }}
      labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
      labelAlign="left"
      wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
    >
      <BaseRow gutter={[16, 16]}>
        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
            <Select
              placeholder="Select Department"
              onChange={(value, option: any) => {
                setSelectedDepartment(value as number);
                form.setFieldsValue({
                  department_id: value,
                  departmentLabel: option?.label,
                });
                fetchCategoryOptions(parseInt(value));
                if (value === 6) fetchServiceChargeOptions();
              }}
            >
              {departmentOptions.map((div) => (
                <Option key={div.value} value={div.value} label={div.label}>
                  {div.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="product_category" label="Category" rules={[{ required: true }]}>
            <Select placeholder="Select Category" onChange={(value) => fetchProductOptions(value)}>
              {categoryOptions.map((cat) => (
                <Option key={cat.value} value={cat.value} label={cat.label}>
                  {cat.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="product" label="Product" rules={[{ required: true }]}>
            <Select placeholder="Select Product" onChange={(value) => fetchVarietyOptions(value)}>
              {productOptions.map((pro) => (
                <Option key={pro.value} value={pro.value} label={pro.label}>
                  {pro.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="product_variety" label="Variety">
            <Select placeholder="Select Variety">
              {varietyOptions.map((varr) => (
                <Option key={varr.value} value={varr.value} label={varr.label}>
                  {varr.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        {selectedDepartment === 6 && (
          <>
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item name="lcl" label="LCL">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </BaseCol>
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item name="fcl" label="FCL">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </BaseCol>
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item name="service_charge" label="Service Charge" rules={[{ required: true }]}>
                <Select
                  placeholder="Select Service"
                  onChange={(value, option: any) => {
                    const selected = serviceChargeOptions.find((s) => s.value === value);
                    form.setFieldsValue({
                      serviceChargeLabel: selected?.label,
                      serviceChargePrice: selected?.unit_price,
                    });
                  }}
                >
                  {serviceChargeOptions.map((service) => (
                    <Option key={service.value} value={service.value}>
                      {service.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </BaseCol>
          </>
        )}

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="qty" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </BaseCol>
      </BaseRow>

      <Form.Item name="departmentLabel" hidden />
      <Form.Item name="serviceChargeLabel" hidden />
      <Form.Item name="serviceChargePrice" hidden />

      <Button type="primary" size="small" onClick={handleAdd}>
        Add to bill
      </Button>
    </Form>
  );
};

export default ExportItemForm;
